import type { PropsWithChildren } from "react";
import { useQuery } from "@tanstack/react-query";
import { VerifiedViewer } from "@/session/product-session";
import { gameContextQueryOptions } from "../game.queries";
import { GameContextProvider } from "./game-context-provider";
import { GamePending } from "./game-pending";
import { GameRedirect } from "./game-redirect";
import { GameRooms } from "./game-rooms";
import { useGameRoute } from "./use-game-route";

export function GameFrame({ children }: PropsWithChildren) {
  const { href } = useGameRoute();
  const query = useQuery(gameContextQueryOptions(href));
  const result = query.data;

  if (!result) {
    if (query.error) throw query.error;

    return <GamePending />;
  }

  let frame;
  let viewer;

  if (result.kind === "navigate") {
    viewer = result.viewer;
    frame = <GameRedirect sourceHref={href} navigation={result.navigation} />;
  } else {
    const game = { ...result.game, publicPath: href };
    viewer = game.viewer;
    frame = (
      <GameContextProvider game={game}>
        {query.error && (
          <output className="block px-6 py-2 text-sm">
            Game access could not be refreshed.{" "}
            <button type="button" onClick={() => void query.refetch()}>
              Try again
            </button>
          </output>
        )}
        <div style={{ display: "contents" }} inert={query.isError}>
          <GameRooms game={game}>{children}</GameRooms>
        </div>
      </GameContextProvider>
    );
  }

  return viewer ? (
    <VerifiedViewer viewer={viewer} fallback={<GamePending />}>
      {frame}
    </VerifiedViewer>
  ) : (
    frame
  );
}
